import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  KeyRound,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Loader2,
  Filter,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient, FailedSyncsResponse, DriverDeadLetterStats } from '../api/client';

export default function FailedSyncs() {
  const [data, setData] = useState<FailedSyncsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const [actionInProgress, setActionInProgress] = useState<Record<string, string>>({});
  const [actionMessages, setActionMessages] = useState<Record<string, string>>({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.getFailedSyncs({
        reason: reasonFilter || undefined,
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load failed syncs');
    } finally {
      setLoading(false);
    }
  }, [reasonFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDriver = (driverId: string) => {
    setExpandedDrivers((prev) => {
      const next = new Set(prev);
      if (next.has(driverId)) {
        next.delete(driverId);
      } else {
        next.add(driverId);
      }
      return next;
    });
  };

  const handleRequestTokenRefresh = async (driver: DriverDeadLetterStats) => {
    setActionInProgress((prev) => ({ ...prev, [driver.driverId]: 'refresh' }));
    try {
      const result = await apiClient.requestTokenRefresh(driver.driverId);
      setActionMessages((prev) => ({ ...prev, [driver.driverId]: result.message }));
    } catch (err) {
      setActionMessages((prev) => ({
        ...prev,
        [driver.driverId]: err instanceof Error ? err.message : 'Failed',
      }));
    } finally {
      setActionInProgress((prev) => {
        const next = { ...prev };
        delete next[driver.driverId];
        return next;
      });
    }
  };

  const handleRetry = async (driver: DriverDeadLetterStats) => {
    setActionInProgress((prev) => ({ ...prev, [driver.driverId]: 'retry' }));
    try {
      const result = await apiClient.retryDriverDeadLetter(driver.driverId);
      setActionMessages((prev) => ({
        ...prev,
        [driver.driverId]: `${result.message}${result.recovered ? ` (${result.recovered} recovered)` : ''}`,
      }));
      // Refresh data after retry
      setTimeout(fetchData, 2000);
    } catch (err) {
      setActionMessages((prev) => ({
        ...prev,
        [driver.driverId]: err instanceof Error ? err.message : 'Failed',
      }));
    } finally {
      setActionInProgress((prev) => {
        const next = { ...prev };
        delete next[driver.driverId];
        return next;
      });
    }
  };

  const getReasonBadgeClass = (reason: string) => {
    switch (reason) {
      case 'auth_expired':
        return 'bg-orange-100 text-orange-800';
      case 'max_retries':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-3 gap-4">
            <div className="h-24 bg-gray-200 rounded" />
            <div className="h-24 bg-gray-200 rounded" />
            <div className="h-24 bg-gray-200 rounded" />
          </div>
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const summary = data?.summary || { totalItems: 0, byReason: {} };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Failed Syncs</h1>
          <p className="text-sm text-gray-500">Dead-lettered items that need attention</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="text-sm text-gray-500">Total Failed</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{summary.totalItems}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Auth Expired</div>
          <div className="text-3xl font-bold text-orange-600 mt-1">
            {summary.byReason['auth_expired'] || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Max Retries</div>
          <div className="text-3xl font-bold text-red-600 mt-1">
            {summary.byReason['max_retries'] || 0}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Filter size={16} className="text-gray-400" />
        <select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Reasons</option>
          <option value="auth_expired">Auth Expired</option>
          <option value="max_retries">Max Retries</option>
          <option value="non_retryable">Non-Retryable (422)</option>
        </select>
      </div>

      {/* Driver Groups */}
      {!data?.drivers?.length ? (
        <div className="card text-center py-12">
          <AlertTriangle size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No failed sync items found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.drivers.map((driver) => (
            <div key={driver.driverId} className="card p-0 overflow-hidden">
              {/* Driver Header */}
              <div
                className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleDriver(driver.driverId)}
              >
                <div className="flex items-center gap-3">
                  {expandedDrivers.has(driver.driverId) ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )}
                  <div>
                    <span className="font-medium text-gray-900">
                      {driver.email || driver.driverId}
                    </span>
                    {driver.email && (
                      <span className="text-xs text-gray-500 ml-2">({driver.driverId})</span>
                    )}
                  </div>
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    {driver.totalItems} items
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {actionMessages[driver.driverId] && (
                    <span className="text-xs text-gray-500 max-w-xs truncate">
                      {actionMessages[driver.driverId]}
                    </span>
                  )}
                  <button
                    onClick={() => handleRequestTokenRefresh(driver)}
                    disabled={!!actionInProgress[driver.driverId]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 disabled:opacity-50 transition-colors"
                    title="Send push notification to refresh token"
                  >
                    {actionInProgress[driver.driverId] === 'refresh' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <KeyRound size={14} />
                    )}
                    Refresh Token
                  </button>
                  <button
                    onClick={() => handleRetry(driver)}
                    disabled={!!actionInProgress[driver.driverId]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 disabled:opacity-50 transition-colors"
                    title="Retry all dead-lettered items"
                  >
                    {actionInProgress[driver.driverId] === 'retry' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RotateCcw size={14} />
                    )}
                    Retry All
                  </button>
                </div>
              </div>

              {/* Expanded Items */}
              {expandedDrivers.has(driver.driverId) && (
                <div className="border-t border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Stop ID
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Reason
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Error
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {driver.items.map((item) => (
                        <tr key={item.syncId}>
                          <td className="px-4 py-2">
                            <span className="badge badge-neutral text-xs">{item.type}</span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                            {item.stopId || '-'}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${getReasonBadgeClass(item.reason)}`}
                            >
                              {item.reason}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">
                            {item.error}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                            {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
